import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { toast } from "sonner";
import type { OrderFormData, OrderLine, Configuration, ConfigAttribute } from "../types";
import type { Json } from "@/integrations/supabase/types";
import type { MaterialTemplateResult } from "@/types/supabase-helpers";
import { queryKeys } from "@/lib/query-keys";


export interface OrderMutationResult {
  id: string;
  newStatus: string;
  statusChanged: boolean;
  oldStatus: string | null;
}

interface UseOrderMutationsProps {
  orderId?: string;
  orderLines: OrderLine[];
  configuration: Configuration | null;
  configurationLineId: string | null;
  originalStatus: string | null;
  onSuccess: (result?: OrderMutationResult) => void;
}

export function useOrderMutations({
  orderId,
  orderLines,
  configuration,
  configurationLineId,
  originalStatus,
  onSuccess,
}: UseOrderMutationsProps) {
  const queryClient = useQueryClient();

  const calculateTotals = () => {
    const subtotal = orderLines.reduce((sum, line) => sum + line.line_total, 0);
    const vat_amount = orderLines.reduce((sum, line) => {
      return sum + (line.line_total * line.vat_rate / 100);
    }, 0);
    const total_amount = subtotal + vat_amount;
    return { subtotal, vat_amount, total_amount };
  };

  const calculateCommission = (data: OrderFormData, total_amount: number) => {
    if (data.partner_id && data.commission_type && data.commission_value) {
      if (data.commission_type === "percentage") {
        return total_amount * (data.commission_value / 100);
      }
      return data.commission_value;
    }
    return null;
  };

  const saveConfiguration = async (configData: Configuration) => {
    console.log("[saveConfig] Starting atomic RPC, model:", configData.productModelId, "components:", configData.components.length);

    // Rakennetaan komponentit JSONB-muotoon RPC:lle
    const componentsJson = configData.components.map((comp) => ({
      model_component_id: comp.modelComponentId,
      material_id: comp.materialId || null,
      quantity: comp.quantity,
      width: comp.width,
      height: comp.height,
      thickness: comp.thickness,
      source_model_name: comp.sourceModelName || null,
    }));

    // Rakennetaan attribuuttiarvot component_index-viittauksella
    const attributeValuesJson = configData.components.flatMap((comp, compIndex) => {
      return comp.attributes
        .filter((attr) => attr.value !== null && attr.value !== "")
        .map((attr) => {
          const entry: Record<string, unknown> = {
            component_index: String(compIndex),
            attribute_id: attr.attributeId,
          };

          if (attr.type === "number") {
            entry.value_number = attr.value as number;
          } else if (attr.type === "date") {
            entry.value_date = attr.value as string;
          } else if (attr.type === "coordinates" || typeof attr.value === "object") {
            entry.value_json = attr.value;
          } else {
            entry.value_text = String(attr.value);
          }

          return entry;
        });
    });

    const { data: result, error: rpcError } = await supabase.rpc(
      "create_configuration_atomic",
      {
        p_product_model_id: configData.productModelId,
        p_config_name: configData.customName?.trim() || configData.productModelName,
        p_components: componentsJson as unknown as Json,
        p_attribute_values: attributeValuesJson as unknown as Json,
      }
    );

    if (rpcError) {
      console.error("[saveConfig] Atomic RPC failed:", rpcError);
      throw rpcError;
    }

    const configId = (result as { config_id: string }).config_id;
    console.log("[saveConfig] Atomic create complete, configId:", configId, "attributes:", attributeValuesJson.length);

    return configId;
  };

  const updateConfiguration = async (existingConfigId: string, configData: Configuration) => {
    console.log("[updateConfig] Starting atomic RPC, configId:", existingConfigId, "components:", configData.components.length);

    // Rakennetaan komponentit JSONB-muotoon RPC:lle
    const componentsJson = configData.components.map((comp) => ({
      model_component_id: comp.modelComponentId,
      material_id: comp.materialId || null,
      quantity: comp.quantity,
      width: comp.width,
      height: comp.height,
      thickness: comp.thickness,
      source_model_name: comp.sourceModelName || null,
    }));

    // Rakennetaan attribuuttiarvot component_index-viittauksella (RPC mappaa oikeisiin id:ihin)
    const attributeValuesJson = configData.components.flatMap((comp, compIndex) => {
      return comp.attributes
        .filter((attr) => attr.value !== null && attr.value !== "")
        .map((attr) => {
          const entry: Record<string, unknown> = {
            component_index: String(compIndex),
            attribute_id: attr.attributeId,
          };

          if (attr.type === "number") {
            entry.value_number = attr.value as number;
          } else if (attr.type === "date") {
            entry.value_date = attr.value as string;
          } else if (attr.type === "coordinates" || typeof attr.value === "object") {
            entry.value_json = attr.value;
          } else {
            entry.value_text = String(attr.value);
          }

          return entry;
        });
    });

    const { data: savedComponentsResult, error: rpcError } = await supabase.rpc(
      "update_configuration_atomic",
      {
        p_config_id: existingConfigId,
        p_product_model_id: configData.productModelId,
        p_config_name: configData.customName?.trim() || configData.productModelName,
        p_components: componentsJson as unknown as Json,
        p_attribute_values: attributeValuesJson as unknown as Json,
      }
    );

    if (rpcError) {
      console.error("[updateConfig] Atomic RPC failed:", rpcError);
      throw rpcError;
    }

    const savedComponents = savedComponentsResult as Array<{ index: number; id: string }>;
    console.log("[updateConfig] Atomic update complete, components:", savedComponents.length, "attributes:", attributeValuesJson.length);

    return existingConfigId;
  };

  const copyProductMaterialTemplates = async (orderIdParam: string, productIds: string[]) => {
    if (productIds.length === 0) return;

    const { data: components } = await supabase
      .from("product_components")
      .select("parent_product_id, component_product_id, default_quantity")
      .in("parent_product_id", productIds);

    const allProductIds = [
      ...productIds,
      ...(components?.map((c) => c.component_product_id) || []),
    ].filter((id, idx, arr) => arr.indexOf(id) === idx);

    const { data: templates, error: templatesError } = await supabase
      .from("product_material_templates")
      .select(`*, materials (unit_cost)`)
      .in("product_id", allProductIds);

    if (!templatesError && templates && templates.length > 0) {
      const materialsToInsert = (templates as MaterialTemplateResult[]).map((template) => {
        const component = components?.find((c) => c.component_product_id === (template as unknown as { product_id: string }).product_id);
        const quantityMultiplier = component?.default_quantity || 1;

        return {
          order_id: orderIdParam,
          material_id: template.material_id,
          quantity: template.default_quantity * quantityMultiplier,
          width: template.default_width,
          length: template.default_length,
          thickness: template.default_thickness,
          unit_cost: template.materials?.unit_cost ?? null,
          notes: template.notes,
        };
      });

      await supabase.from("order_materials").insert(materialsToInsert);
    }
  };

  const createOrderMutation = useMutation({
    mutationFn: async (data: OrderFormData) => {
      try {
      if (orderLines.length === 0) {
        throw new Error("Tilausrivejä oltava vähintään 1 kpl");
      }

      const { subtotal, vat_amount, total_amount } = calculateTotals();
      const commission_amount = calculateCommission(data, total_amount);

      const { data: order, error: orderError } = await supabase
        .from("orders")
        .insert({
          order_number: "",
          customer_id: data.customer_id,
          partner_id: data.partner_id || null,
          cemetery_id: data.cemetery_id || null,
          order_date: format(data.order_date, "yyyy-MM-dd"),
          installation_date: data.installation_date ? format(data.installation_date, "yyyy-MM-dd") : null,
          delivery_date: data.delivery_date ? format(data.delivery_date, "yyyy-MM-dd") : null,
          order_type: data.order_type,
          fulfillment_type: data.fulfillment_type,
          status: data.status,
          notes: data.notes || null,
          subtotal,
          vat_amount,
          total_amount,
          commission_type: data.commission_type || null,
          commission_value: data.commission_value || null,
          commission_amount,
          requires_cemetery_approval: data.requires_cemetery_approval ?? false,
          cemetery_approval_status: data.requires_cemetery_approval ? 'awaiting_request' : 'not_required',
        })
        .select()
        .single();

      if (orderError) throw orderError;

      let savedConfigurationId: string | null = null;
      if (configuration) {
        savedConfigurationId = await saveConfiguration(configuration);
      }

      const linesToInsert = orderLines.map((line, index) => ({
        order_id: order.id,
        product_id: line.product_id || null,
        configuration_id: line.id === configurationLineId ? savedConfigurationId : null,
        description: line.description,
        quantity: line.quantity,
        unit_price: line.unit_price,
        vat_rate: line.vat_rate,
        line_total: line.line_total,
        position: index + 1,
        price_includes_vat: line.price_includes_vat,
        work_details: (line.work_details ?? null) as Json,
      }));

      const { error: linesError } = await supabase.from("order_lines").insert(linesToInsert);
      if (linesError) throw linesError;

      const productIds = orderLines.filter((line) => line.product_id).map((line) => line.product_id as string);
      await copyProductMaterialTemplates(order.id, productIds);

      return order;
      } catch (error) {
        console.error("[createOrder] Unexpected error:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      toast.success("Tilaus luotu onnistuneesti");
      onSuccess();
    },
    onError: (error: Error) => {
      toast.error("Virhe", { description: error.message });
    },
  });

  const updateOrderMutation = useMutation({
    mutationFn: async (data: OrderFormData) => {
      try {
      if (!orderId) throw new Error("Order ID puuttuu");
      if (orderLines.length === 0) {
        throw new Error("Tilausrivejä oltava vähintään 1 kpl");
      }

      const { subtotal, vat_amount, total_amount } = calculateTotals();
      const commission_amount = calculateCommission(data, total_amount);

      // Fetch current approval status to avoid resetting already approved/pending statuses
      const { data: currentOrder } = await supabase
        .from("orders")
        .select("cemetery_approval_status, requires_cemetery_approval")
        .eq("id", orderId)
        .single();

      const currentApprovalStatus = currentOrder?.cemetery_approval_status || 'not_required';
      const wasRequiringApproval = currentOrder?.requires_cemetery_approval ?? false;
      
      // Determine new approval status
      let newApprovalStatus = currentApprovalStatus;
      if (data.requires_cemetery_approval && !wasRequiringApproval) {
        // Turning on approval requirement - set to awaiting_request (not yet sent)
        newApprovalStatus = 'awaiting_request';
      } else if (!data.requires_cemetery_approval) {
        // Turning off approval requirement
        newApprovalStatus = 'not_required';
      }
      // If was already requiring and still requiring, keep current status

      const { error: orderError } = await supabase
        .from("orders")
        .update({
          customer_id: data.customer_id,
          partner_id: data.partner_id || null,
          cemetery_id: data.cemetery_id || null,
          order_date: format(data.order_date, "yyyy-MM-dd"),
          installation_date: data.installation_date ? format(data.installation_date, "yyyy-MM-dd") : null,
          delivery_date: data.delivery_date ? format(data.delivery_date, "yyyy-MM-dd") : null,
          order_type: data.order_type,
          fulfillment_type: data.fulfillment_type,
          status: data.status,
          notes: data.notes || null,
          subtotal,
          vat_amount,
          total_amount,
          commission_type: data.commission_type || null,
          commission_value: data.commission_value || null,
          commission_amount,
          requires_cemetery_approval: data.requires_cemetery_approval ?? false,
          cemetery_approval_status: newApprovalStatus,
        })
        .eq("id", orderId);

      if (orderError) throw orderError;

      let savedConfigurationId: string | null = null;
      const existingConfigLine = orderLines.find((line) => line.configuration_id);
      const existingConfigId = existingConfigLine?.configuration_id;

      if (configuration) {
        if (existingConfigId) {
          savedConfigurationId = await updateConfiguration(existingConfigId, configuration);
        } else {
          savedConfigurationId = await saveConfiguration(configuration);
        }
      }

      const linesJson = orderLines.map((line, index) => ({
        product_id: line.product_id || null,
        configuration_id: line.id === configurationLineId ? savedConfigurationId : (line.configuration_id || null),
        description: line.description,
        quantity: line.quantity,
        unit_price: line.unit_price,
        vat_rate: line.vat_rate,
        line_total: line.line_total,
        position: index + 1,
        price_includes_vat: line.price_includes_vat,
        work_details: line.work_details ?? null,
      }));

      const { error: linesError } = await supabase.rpc(
        "replace_order_lines_atomic",
        {
          p_order_id: orderId,
          p_lines: linesJson as unknown as Json,
        }
      );
      if (linesError) throw linesError;

      // Palauta statusChanged-tieto OrderDialogille, joka hoitaa sähköpostin lähetyksen
      return { 
        id: orderId, 
        newStatus: data.status,
        statusChanged: originalStatus !== null && 
                       originalStatus !== data.status && 
                       data.status !== "draft",
        oldStatus: originalStatus
      };
      } catch (error) {
        console.error("[updateOrder] Unexpected error:", error);
        throw error;
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.stats() });
      queryClient.invalidateQueries({ queryKey: queryKeys.workshop.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.installations.upcoming() });
      toast.success("Tilaus päivitetty onnistuneesti");

      // Sähköpostin lähetys hoidetaan nyt OrderDialog/index.tsx:ssä
      // käyttäen keskitettyä useOrderStatusMutation hookia
      onSuccess(result);
    },
    onError: (error: Error) => {
      toast.error("Virhe", { description: error.message });
    },
  });

  return {
    createOrderMutation,
    updateOrderMutation,
  };
}
